import { useEffect, useState } from 'react'
import { app } from '../contexts/AuthContext'

async function sha256Hex(s: string): Promise<string> {
  const te = new TextEncoder()
  const buf = te.encode(s)
  const hash = await crypto.subtle.digest('SHA-256', buf)
  const arr = Array.from(new Uint8Array(hash))
  return arr.map(b => b.toString(16).padStart(2, '0')).join('')
}

type Overview = { totalUsers: number; paidOrders: number; activeMembers: number; pv?: number; uv?: number; todayPV?: number; todayUV?: number; todayLoginUsers?: number; totalRevenue?: number; todayRevenue?: number }
type MemberItem = { uid: string | null; email: string; displayName: string; memberType: 'vip' | 'normal'; vipUntil?: string | null; plan?: string | null; lastOrderNo?: string | null }
type OrderItem = { orderNo: string; uid: string; amount: number; plan?: string; paidAt?: string; alipayTradeNo?: string }

export function Admin() {
  const [data, setData] = useState<Overview | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [pwd, setPwd] = useState('')
  const [loginOk, setLoginOk] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)
  const [members, setMembers] = useState<MemberItem[]>([])
  const [orders, setOrders] = useState<OrderItem[]>([])
  const [membersPage, setMembersPage] = useState(1)
  const [ordersPage, setOrdersPage] = useState(1)
  const [membersTotal, setMembersTotal] = useState(0)
  const [ordersTotal, setOrdersTotal] = useState(0)
  const pageSize = 20

  const ADMIN_HASH = '2ed6ecbb31dbbc6a8e92f1d7127cf70ff9b62ff2100327e0a94d62e91f5af69b'

  const handleLogin = async () => {
    setLoggingIn(true)
    setErr(null)
    try {
      const h = await sha256Hex(pwd)
      if (h === ADMIN_HASH) {
        setLoginOk(true)
      } else {
        setErr('密码错误')
      }
    } catch (e: any) {
      setErr(e?.message || '登录异常')
    } finally {
      setLoggingIn(false)
    }
  }

  const callCF = async (name: string, data?: any): Promise<any> => {
    const r: any = await (app as any).callFunction?.({ name, data })
    const res = r?.result
    if (!res) return null
    if (res.body) {
      try { return JSON.parse(res.body) } catch { return res }
    }
    return res
  }

  const loadOverview = async () => {
    setLoading(true)
    setErr(null)
    setData(null)
    try {
      const j = await callCF('adminOverview', {})
      if (!j) { throw new Error('not found') }
      const out: Overview = {
        totalUsers: Number(j?.totalUsers ?? 0),
        paidOrders: Number(j?.paidOrdersCount ?? j?.paidOrders ?? 0),
        activeMembers: Number(j?.activeMembers ?? 0),
        todayPV: Number(j?.pv ?? j?.todayPV ?? 0),
        todayUV: Number(j?.uv ?? j?.todayUV ?? 0),
        todayLoginUsers: Number(j?.todayLoginUsers ?? 0),
        totalRevenue: Number(j?.totalRevenue ?? 0),
        todayRevenue: Number(j?.todayRevenue ?? 0)
      }
      setData(out)
    } catch (e: any) {
      setErr(e?.message || 'error')
      const out: Overview = { totalUsers: 0, paidOrders: 0, activeMembers: 0, todayPV: 0, todayUV: 0, todayLoginUsers: 0 }
      setData(out)
    } finally {
      setLoading(false)
    }
  }

  const loadMembers = async (page: number) => {
    try {
      const j = await callCF('adminMembers', { page, limit: pageSize })
      setMembersTotal(Number(j?.total ?? 0))
      const arr: MemberItem[] = Array.isArray(j?.items) ? j.items.map((x: any) => ({
        uid: x.uid || null,
        email: x.email || '',
        displayName: x.displayName || '',
        memberType: (x.memberType === 'vip' ? 'vip' : 'normal'),
        vipUntil: x.vipUntil || null,
        plan: x.plan || null,
        lastOrderNo: x.lastOrderNo || null
      })) : []
      setMembers(arr)
      setMembersPage(page)
    } catch (e: any) {
      setErr(e?.message || '加载会员失败')
    }
  }

  const loadOrders = async (page: number) => {
    try {
      const j = await callCF('adminOrders', { page, limit: pageSize })
      setOrdersTotal(Number(j?.total ?? 0))
      const arr: OrderItem[] = Array.isArray(j?.items) ? j.items.map((x: any) => ({
        orderNo: x.orderNo,
        uid: x.uid,
        amount: Number(x.amount || 0),
        plan: x.plan || '',
        paidAt: x.paidAt || '',
        alipayTradeNo: x.alipayTradeNo || ''
      })) : []
      setOrders(arr)
      setOrdersPage(page)
    } catch (e: any) {
      setErr(e?.message || '加载订单失败')
    }
  }

  useEffect(() => {
    if (loginOk) { loadOverview(); loadMembers(1); loadOrders(1) }
  }, [loginOk])

  return (
    <div className="p-8">
      <div className="text-2xl mb-6">管理员总览</div>
      <div className="space-y-4">
        {!loginOk && (
          <div className="max-w-sm space-y-3">
            <input
              type="password"
              className="border px-3 py-2 w-full"
              placeholder="请输入管理员密码"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
            />
            <button className="border px-4 py-2" onClick={handleLogin} disabled={loggingIn || !pwd}>登录</button>
          </div>
        )}
        {loading && <div>加载中...</div>}
        {err && <div className="text-red-500">{err}</div>}
        {loginOk && data && (
          <div className="grid grid-cols-5 gap-6">
            <div className="border p-4"><div className="text-gray-500">总用户数</div><div className="text-3xl">{data.totalUsers}</div></div>
            <div className="border p-4"><div className="text-gray-500">有效会员</div><div className="text-3xl">{data.activeMembers}</div></div>
            <div className="border p-4"><div className="text-gray-500">已付费订单</div><div className="text-3xl">{data.paidOrders}</div></div>
            <div className="border p-4"><div className="text-gray-500">今日PV</div><div className="text-3xl">{data.todayPV}</div></div>
            <div className="border p-4"><div className="text-gray-500">今日UV</div><div className="text-3xl">{data.todayUV}</div></div>
          </div>
        )}
        {loginOk && (
          <div className="flex gap-3">
            <button className="border px-4 py-2" onClick={loadOverview}>刷新概览</button>
            <button className="border px-4 py-2" onClick={() => loadMembers(membersPage)}>刷新会员</button>
            <button className="border px-4 py-2" onClick={() => loadOrders(ordersPage)}>刷新订单</button>
          </div>
        )}
        {loginOk && (
          <div className="mt-8">
            <div className="text-xl mb-3">会员列表</div>
            <div className="border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">显示名</th>
                    <th className="text-left p-2">邮箱</th>
                    <th className="text-left p-2">会员</th>
                    <th className="text-left p-2">到期</th>
                    <th className="text-left p-2">套餐</th>
                    <th className="text-left p-2">最近订单号</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-2">{(membersPage - 1) * pageSize + i + 1}</td>
                      <td className="p-2">{m.displayName}</td>
                      <td className="p-2">{m.email}</td>
                      <td className="p-2">{m.memberType === 'vip' ? 'VIP' : '普通'}</td>
                      <td className="p-2">{m.vipUntil ? String(m.vipUntil).slice(0,10) : '-'}</td>
                      <td className="p-2">{m.plan || '-'}</td>
                      <td className="p-2">{m.lastOrderNo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <button className="border px-3 py-1" disabled={membersPage<=1} onClick={() => loadMembers(membersPage-1)}>上一页</button>
              <div>第 {membersPage} 页 / 共 {Math.max(1, Math.ceil(membersTotal / pageSize))} 页</div>
              <button className="border px-3 py-1" disabled={(membersPage*pageSize)>=membersTotal} onClick={() => loadMembers(membersPage+1)}>下一页</button>
            </div>
          </div>
        )}
        {loginOk && (
          <div className="mt-8">
            <div className="text-xl mb-3">付费订单</div>
            <div className="border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">订单号</th>
                    <th className="text-left p-2">UID</th>
                    <th className="text-left p-2">金额</th>
                    <th className="text-left p-2">套餐</th>
                    <th className="text-left p-2">支付时间</th>
                    <th className="text-left p-2">支付宝交易号</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-2">{o.orderNo}</td>
                      <td className="p-2">{o.uid}</td>
                      <td className="p-2">{o.amount}</td>
                      <td className="p-2">{o.plan || '-'}</td>
                      <td className="p-2">{o.paidAt ? String(o.paidAt).replace('T',' ').slice(0,19) : '-'}</td>
                      <td className="p-2">{o.alipayTradeNo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <button className="border px-3 py-1" disabled={ordersPage<=1} onClick={() => loadOrders(ordersPage-1)}>上一页</button>
              <div>第 {ordersPage} 页 / 共 {Math.max(1, Math.ceil(ordersTotal / pageSize))} 页</div>
              <button className="border px-3 py-1" disabled={(ordersPage*pageSize)>=ordersTotal} onClick={() => loadOrders(ordersPage+1)}>下一页</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
